import { DESTRUCTION_THRESHOLD, TAKEOVER_LOYALTY_CEILING, UPRISING_LOYALTY, } from './constants.js';
import { liveCount, totalIntegrity } from './metrics.js';
// ---------------------------------------------------------------------------
// Losing.
//
// Three ways out, and they are meant to feel completely different: bombed flat,
// overthrown, or occupied. The third is deliberately close to impossible.
// ---------------------------------------------------------------------------
export function checkDefeat(city) {
    if (city.defeated)
        return city.defeated;
    if (totalIntegrity(city) < DESTRUCTION_THRESHOLD)
        return 'DESTRUCTION';
    if (city.populace.loyalty <= UPRISING_LOYALTY)
        return 'UPRISING';
    return null;
}
/**
 * Takeover requires every one of these to be true at the same moment. It is not
 * a thing you stumble into; it is a campaign you run for several rounds and
 * usually fail. That is the point.
 */
export function canBeTakenOver(city) {
    const blockers = [];
    const garrison = liveCount(city, 'barracks');
    if (garrison > 0)
        blockers.push(`${garrison} barracks still holding`);
    const interceptors = city.defences.filter((d) => d.type !== 'shelter' && d.hp > 0 && d.offlineFor === 0).length;
    if (interceptors > 0)
        blockers.push(`${interceptors} air defence unit(s) still live`);
    if (city.populace.loyalty >= TAKEOVER_LOYALTY_CEILING) {
        blockers.push(`population still loyal (${Math.round(city.populace.loyalty)})`);
    }
    if (liveCount(city, 'command') > 0)
        blockers.push('command bunker intact');
    return { ok: blockers.length === 0, blockers };
}
/** Applies a takeover if it is actually possible. Returns whether it landed. */
export function attemptTakeover(city) {
    const check = canBeTakenOver(city);
    if (!check.ok)
        return false;
    city.defeated = 'TAKEOVER';
    return true;
}
/**
 * Each cluster's battle, in cluster order. Works on anything with a cluster, a
 * side and a defeated flag, so a client can read it off its own view.
 */
export function clusterStatus(cities) {
    const by = new Map();
    for (const c of cities) {
        // A reserve fights in no cluster, and a city that fell in an earlier stage
        // is off the board: both are -1.
        if (c.cluster < 0)
            continue;
        const s = by.get(c.cluster) ?? { cluster: c.cluster, region: c.region ?? 0, west: 0, east: 0, resolved: false, heldBy: null };
        if (!c.defeated)
            s[c.faction === 'WEST' ? 'west' : 'east']++;
        by.set(c.cluster, s);
    }
    for (const s of by.values()) {
        s.resolved = s.west === 0 || s.east === 0;
        s.heldBy = !s.resolved ? null : s.west > 0 ? 'WEST' : s.east > 0 ? 'EAST' : null;
    }
    return [...by.values()].sort((a, b) => a.cluster - b.cluster);
}
/** Each region's clusters and whether any is still being fought, in region order. */
export function regionStatus(cities) {
    const by = new Map();
    for (const s of clusterStatus(cities)) {
        const r = by.get(s.region) ?? { region: s.region, clusters: [], active: 0, resolved: true };
        r.clusters.push(s.cluster);
        if (!s.resolved) {
            r.active++;
            r.resolved = false;
        }
        by.set(s.region, r);
    }
    return [...by.values()].sort((a, b) => a.region - b.region);
}
/** A region as a commander reads it: A, B, C... */
export function regionName(region) {
    return region < 0 ? '—' : String.fromCharCode(65 + (region % 26));
}
/**
 * A cluster as a commander reads it: its region's letter and its place in the
 * region, B2 for the second cluster of region B. Clusters are numbered in
 * region order, so the place is counted from the region's first.
 */
export function clusterName(cluster, cities) {
    if (cluster < 0)
        return 'reserve';
    const region = cities.find((c) => c.cluster === cluster)?.region ?? 0;
    const first = Math.min(...cities.filter((c) => c.cluster >= 0 && (c.region ?? 0) === region).map((c) => c.cluster));
    return `${regionName(region)}${cluster - (Number.isFinite(first) ? first : cluster) + 1}`;
}
/** Sweep every city, mark the fallen, and see if a side has run out. */
export function evaluateMatch(match) {
    const defeats = [];
    for (const city of match.cities) {
        if (city.defeated)
            continue;
        const reason = checkDefeat(city);
        if (reason) {
            city.defeated = reason;
            defeats.push({ cityId: city.id, reason });
        }
    }
    const eastAlive = match.cities.some((c) => c.faction === 'EAST' && !c.defeated);
    const westAlive = match.cities.some((c) => c.faction === 'WEST' && !c.defeated);
    let winner = null;
    if (!eastAlive && !westAlive)
        winner = null; // mutual ruin; the match just ends
    else if (!eastAlive)
        winner = 'WEST';
    else if (!westAlive)
        winner = 'EAST';
    return { defeats, winner };
}
//# sourceMappingURL=victory.js.map